import * as React from "react";

import { CurrentUserContext } from "@v2/contexts/CurrentUserContext";
import { initUserBasedAnalytics } from "@v2/features/infra/analytics/init";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { userRouter } from "@v2/trpc/routes/user";
import { storeToken } from "@v2/utils/store-token";
import { useLocation } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import { z } from "zod";

export const CurrentUserProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { user, isAuthenticated, isLoading, isError } = useFetchCurrentUser();
  return (
    <CurrentUserContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isError,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};

const MAX_PYLON_INITIALIZE_ATTEMPTS = 10;

const parseAndValidateUuid = (value: string | null): string | null => {
  const result = z.uuid().safeParse(value);
  return result.success ? result.data : null;
};

// Note (Noah, 2025-10-23): These are specifically outside of the component because we only
// want to set them on a full page refresh. We don't want to get into a state where because
// the local storage values were updated, we refetch the user, and have extra loading states
// for no reason - these are used purely for the initial page load.
const lastVisitedWorkspaceId = parseAndValidateUuid(
  localStorage.getItem("replo.workspace.lastvisitedWorkspaceId"),
);
const lastVisitedProjectId = parseAndValidateUuid(
  localStorage.getItem("replo.project.lastVisitedProjectId"),
);

function useFetchCurrentUser() {
  const { pathname } = useLocation();
  const isOnboarding = pathname.includes("/onboarding");
  const isSharedTemplateRoute = pathname.startsWith("/share/page/");

  const {
    data: user,
    isLoading,
    isError,
    isSuccess,
  } = userRouter.get.useQuery({
    lastVisitedWorkspaceId,
    lastVisitedProjectId,
  });
  const userIsInitialized = React.useRef(false);

  React.useEffect(() => {
    // Note (Noah, 2024-08-21): Only initialize the user-based analytics once
    // throughout the lifetime of the page, since we don't want to re-initialize
    // if multiple components are using this hook.
    if (isSuccess && user && !userIsInitialized.current) {
      initUserBasedAnalytics(user);
      userIsInitialized.current = true;
    }
  }, [isSuccess, user]);

  React.useEffect(() => {
    if (isError) {
      void storeToken(null);
    }
  }, [isError]);

  const isSupportChatEnabled =
    !isLocalhost() && !isOnboarding && !isSharedTemplateRoute;
  const logEvent = useLogAnalytics();

  const { data: pylonIdentityData } = userRouter.getPylonIdentityHash.useQuery(
    undefined,
    {
      enabled: Boolean(user) && isSupportChatEnabled,
    },
  );

  React.useEffect(() => {
    // This effect handles the Pylon widget script injection and configuration
    if (user && isSupportChatEnabled) {
      window.pylon = window.pylon || {
        chat_settings: {
          app_id: "efe32725-24c5-4f17-bb77-42f06cc70918",
          email: user.email,
          name: user.name ?? user.email,
        },
      };

      // Set the email hash for Pylon identity verification
      if (pylonIdentityData?.emailHash && window.pylon?.chat_settings) {
        window.pylon.chat_settings.email_hash = pylonIdentityData.emailHash;
      }

      let pylonCheckAttempts = 0;

      const configurePylon = () => {
        if (window.Pylon) {
          window.Pylon("onShow", function () {
            logEvent("support.chat.open", {
              source: "pylon",
              userEmail: user.email,
              userName: user.name ?? user.email,
            });
          });
        } else if (pylonCheckAttempts < MAX_PYLON_INITIALIZE_ATTEMPTS) {
          // Note (Juan, 09/11/2024): The Pylon script is loaded asynchronously, which means it might not be
          // immediately available after the script's "load" event fires. To handle this,
          // we implement a retry mechanism using setTimeout. This function will keep
          // checking for the window.Pylon object every 500ms until it becomes available.
          // This ensures that we can configure the Pylon chat as soon as it's ready,
          // without risking trying to use it before it's fully loaded.
          pylonCheckAttempts++;
          setTimeout(configurePylon, 500);
        } else {
          console.warn("Failed to load Pylon after multiple attempts");
        }
      };

      configurePylon();
    }
  }, [user, isSupportChatEnabled, logEvent, pylonIdentityData?.emailHash]);

  return {
    user: user ?? undefined,
    isAuthenticated: Boolean(user?.id),
    isLoading,
    isError,
  };
}
